import React, { Fragment, useEffect, useState, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { 
    Bars3BottomLeftIcon, 
    Bars4Icon, 
    ClipboardIcon, 
    ClockIcon, 
    EyeIcon,
    EyeSlashIcon,
    InformationCircleIcon,
    PencilIcon,
    PlusCircleIcon,
    SpeakerWaveIcon,
    VideoCameraIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import { PlayCircleIcon, PauseCircleIcon, MinusCircleIcon } from "@heroicons/react/20/solid";
import { formatTime } from "../../utils/calcs";
import { getTranscriptionWithEmotions, checkEmotionDataAvailable, updateTranscription } from "../../Requests/EnhancedTranscriptionRequests";
import { v4 as uuidv4 } from 'uuid';

export default function EnhancedTranscriptionViewer({ 
    handleClose, 
    analysisParentUuid, 
    analysisParent = null,
    view_mode = "private"
}) {
    const [open, setOpen] = useState(true);
    const { t } = useTranslation();
    const [transcriptionData, setTranscriptionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewGrouped, setViewGrouped] = useState(false);
    const [timeVisibility, setTimeVisibility] = useState(true);
    const [emotionVisibility, setEmotionVisibility] = useState(true);
    const [emotionAvailable, setEmotionAvailable] = useState({ hasAnyEmotions: false });
    const audioRef = useRef(null);
    
    // Edit mode states
    const [mode, setMode] = useState("view");
    const [editableTranscriptions, setEditableTranscriptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Audio player states
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const intervalRef = useRef(null);
    
    const playSpeeds = [
        { "name": "0.25", "speed": 0.25 },
        { "name": "0.5", "speed": 0.5 },
        { "name": "0.75", "speed": 0.75 },
        { "name": "1", "speed": 1 },
        { "name": "1.25", "speed": 1.25 },
        { "name": "1.5", "speed": 1.5 }
    ];

    useEffect(() => {
        loadTranscriptionData();
    }, [analysisParentUuid]);

    const loadTranscriptionData = async (skipLoadingState = false) => {
        try {
            if (!skipLoadingState) {
                setLoading(true);
            }
            
            // Check emotion availability first
            const emotionCheck = await checkEmotionDataAvailable(analysisParentUuid);
            setEmotionAvailable(emotionCheck);
            
            // Get transcription with emotions if available, otherwise basic transcription
            const response = await getTranscriptionWithEmotions(analysisParentUuid);
            setTranscriptionData(response);
            
        } catch (err) {
            console.error('Error loading transcription:', err);
            console.error('Error details:', {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
                analysisParentUuid
            });
            setError(`Failed to load transcription data: ${err.response?.status || 'Unknown error'} - ${err.response?.data?.error || err.message}`);
        } finally {
            if (!skipLoadingState) {
                setLoading(false);
            }
        }
    };

    const closeModal = () => {
        setOpen(false);
    };

    const closeAndDestroy = () => {
        handleClose();
        setOpen(false);
    };

    const reopenModal = () => {
        setOpen(true);
    };

    const seekToTime = async (timeInSeconds) => {
        if (audioRef.current) {
            audioRef.current.currentTime = timeInSeconds;
            // Update the current time state immediately
            setCurrentTime(timeInSeconds);
            // Update the progress bar as well
            const newProgress = (timeInSeconds / audioRef.current.duration) * 400;
            setProgress(newProgress);
            try {
                await audioRef.current.play();
            } catch (error) {
                // Auto-play blocked by browser - expected behavior
            }
        }
    };


    const groupBySpeaker = (utterances) => {
        const grouped = [];
        let currentGroup = null;

        utterances.forEach((utterance, index) => {
            const speaker = utterance.speaker || `Speaker ${utterance.channel || 'Unknown'}`;
            
            if (currentGroup && currentGroup.speaker === speaker) {
                currentGroup.utterances.push(utterance);
                currentGroup.endTime = utterance.end || currentGroup.endTime;
            } else {
                if (currentGroup) {
                    grouped.push(currentGroup);
                }
                currentGroup = {
                    speaker,
                    startTime: utterance.start || 0,
                    endTime: utterance.end || utterance.start || 0,
                    utterances: [utterance]
                };
            }
            
            if (index === utterances.length - 1 && currentGroup) {
                grouped.push(currentGroup);
            }
        });

        return grouped;
    };

    const formatTimestamp = (timestamp) => {
        // Convert milliseconds to seconds if needed
        // Most transcription systems use milliseconds, so we assume ms and convert to seconds
        const seconds = timestamp / 1000;
        return formatTime(seconds, 0);
    };



    // Function to render emotion badges grouped by type with single icon per group
    const renderEmotionBadges = (emotions) => {
        if (!emotions || (!emotions.audio && !emotions.video)) {
            return null;
        }

        const renderEmotionGroup = (emotionData, type) => {
            if (!emotionData?.probabilities) return null;

            const probabilities = emotionData.probabilities;
            
            // Filter out emotions with low confidence and sort by percentage
            let significantEmotions = Object.entries(probabilities)
                .filter(([emotion, value]) => value && value > 0.1) // Only show emotions > 10%
                .sort(([,a], [,b]) => b - a) // Sort by percentage descending
                .slice(0, 3); // Show max 3 emotions per type
            
            // If we don't have at least 3 emotions, lower the threshold to get more
            if (significantEmotions.length < 3) {
                significantEmotions = Object.entries(probabilities)
                    .filter(([emotion, value]) => value && value > 0.01) // Lower to 1% threshold
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 3);
            }

            if (significantEmotions.length === 0) return null;

            const IconComponent = type === 'audio' ? SpeakerWaveIcon : VideoCameraIcon;
            const typeColor = type === 'audio' ? 'text-blue-600' : 'text-green-600';

            return (
                <div key={type} className="flex items-center gap-1">
                    <IconComponent className={`w-4 h-4 ${typeColor} flex-shrink-0`} />
                    <div className="flex flex-wrap gap-1">
                        {significantEmotions.map(([emotion, probability]) => {
                            const percentage = Math.round(probability * 100);
                            const emotionColor = getEmotionBadgeColor(emotion);
                            
                            return (
                                <span
                                    key={emotion}
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${emotionColor}`}
                                >
                                    {emotion.charAt(0).toUpperCase() + emotion.slice(1)} {percentage}%
                                </span>
                            );
                        })}
                    </div>
                </div>
            );
        };

        const audioGroup = renderEmotionGroup(emotions.audio, 'audio');
        const videoGroup = renderEmotionGroup(emotions.video, 'video');

        const groups = [videoGroup, audioGroup].filter(Boolean); // Video first, then audio

        if (groups.length === 0) {
            return null;
        }

        return (
            <div className="flex flex-col gap-1 mt-1">
                {groups}
            </div>
        );
    };

    // Function to render sentence-level content with individual emotions
    const renderSentenceContent = (utterance) => {
        // If sentences are available, render them individually
        if (utterance.sentences && utterance.sentences.length > 0) {
            return (
                <div className="space-y-2">
                    {utterance.sentences.map((sentence, sentenceIndex) => (
                        <div key={`sentence-${sentenceIndex}-${sentence.start}`} className="border-l-2 border-gray-200 pl-3">
                            <div className="text-base">{sentence.text}</div>
                            {emotionAvailable.hasAnyEmotions && emotionVisibility && sentence.emotions && (
                                <div className="mt-1">
                                    {renderEmotionBadges(sentence.emotions)}
                                </div>
                            )}
                            {/* Optional: Show sentence timing */}
                            <div className="text-xs text-gray-400 mt-1">
                                {sentence.start.toFixed(2)}s - {sentence.end.toFixed(2)}s
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        // Fallback to original utterance-level rendering
        return (
            <div>
                <span className="text-base">{utterance.text}</span>
                {emotionAvailable.hasAnyEmotions && emotionVisibility && utterance.emotions && renderEmotionBadges(utterance.emotions)}
            </div>
        );
    };

    // Function to get badge colors for emotions
    const getEmotionBadgeColor = (emotion) => {
        const colors = {
            happiness: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
            sadness: 'bg-blue-100 text-blue-800 border border-blue-300',
            anger: 'bg-red-100 text-red-800 border border-red-300',
            fear: 'bg-violet-100 text-violet-800 border border-violet-300',
            surprise: 'bg-orange-100 text-orange-800 border border-orange-300',
            disgust: 'bg-lime-100 text-lime-800 border border-lime-300',
            neutral: 'bg-gray-100 text-gray-800 border border-gray-300'
        };
        return colors[emotion.toLowerCase()] || 'bg-gray-100 text-gray-800 border border-gray-300';
    };

    // Function to handle edit mode time formatting
    const transformTimeFormat = (staticPart, index) => {
        // Handle both formats: with and without UUID prefix
        const matches = staticPart.match(/<[^>]+>\s*\[(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\]\s+((?:Speaker\s+)?[\w\s]+):/) || 
                        staticPart.match(/\[(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\]\s+((?:Speaker\s+)?[\w\s]+):/);
        if (matches) {
            const startTimeSeconds = parseFloat(matches[1]);
            const endTimeSeconds = parseFloat(matches[2]);
            const speakerName = matches[3].trim();

            // Format time as [hours, minutes, seconds] for editing
            const formatTimeArray = (timeSeconds) => {
                const hours = Math.floor(timeSeconds / 3600);
                const minutes = Math.floor((timeSeconds % 3600) / 60);
                const seconds = Math.floor(timeSeconds % 60);
                return [hours.toString(), minutes.toString().padStart(2, '0'), seconds.toString().padStart(2, '0')];
            };

            const startTime = formatTimeArray(startTimeSeconds);
            const endTime = formatTimeArray(endTimeSeconds);

            const handleTimeChange = (value, timeType, unit, isEndTime) => {
                const updatedLines = [...editableTranscriptions];
                const line = updatedLines[index];
                if (!line) return;

                // Parse current static part to get current times and speaker (handle UUID prefix)
                const currentMatches = line.staticPart.match(/<[^>]+>\s*\[(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\]\s+((?:Speaker\s+)?[\w\s]+):/) || 
                                     line.staticPart.match(/\[(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\]\s+((?:Speaker\s+)?[\w\s]+):/);
                if (!currentMatches) return;

                let startTimeSeconds = parseFloat(currentMatches[1]);
                let endTimeSeconds = parseFloat(currentMatches[2]);
                const speakerName = currentMatches[3].trim();
                
                // Extract UUID prefix if it exists
                const uuidMatch = line.staticPart.match(/(<[^>]+>\s*)/);
                const uuidPrefix = uuidMatch ? uuidMatch[1] : '';

                // Convert value to number and update the appropriate time
                const numValue = parseInt(value) || 0;
                
                if (isEndTime) {
                    // Update end time - proper time calculation
                    const currentHours = Math.floor(endTimeSeconds / 3600);
                    const currentMinutes = Math.floor((endTimeSeconds % 3600) / 60);
                    const currentSeconds = endTimeSeconds % 60;
                    
                    if (unit === 0) { // hours
                        endTimeSeconds = (numValue * 3600) + (currentMinutes * 60) + currentSeconds;
                    } else if (unit === 1) { // minutes
                        endTimeSeconds = (currentHours * 3600) + (numValue * 60) + currentSeconds;
                    } else if (unit === 2) { // seconds
                        endTimeSeconds = (currentHours * 3600) + (currentMinutes * 60) + numValue;
                    }
                } else {
                    // Update start time - proper time calculation
                    const currentHours = Math.floor(startTimeSeconds / 3600);
                    const currentMinutes = Math.floor((startTimeSeconds % 3600) / 60);
                    const currentSeconds = startTimeSeconds % 60;
                    
                    if (unit === 0) { // hours
                        startTimeSeconds = (numValue * 3600) + (currentMinutes * 60) + currentSeconds;
                    } else if (unit === 1) { // minutes
                        startTimeSeconds = (currentHours * 3600) + (numValue * 60) + currentSeconds;
                    } else if (unit === 2) { // seconds
                        startTimeSeconds = (currentHours * 3600) + (currentMinutes * 60) + numValue;
                    }
                }

                // Ensure times are valid
                startTimeSeconds = Math.max(0, startTimeSeconds);
                endTimeSeconds = Math.max(startTimeSeconds, endTimeSeconds);

                // Update the static part with new times, preserving UUID prefix
                updatedLines[index] = {
                    ...line,
                    staticPart: `${uuidPrefix}[${startTimeSeconds.toFixed(2)}-${endTimeSeconds.toFixed(2)}] ${speakerName}:`
                };

                setEditableTranscriptions(updatedLines);
            };

            const handleSpeakerChange = (newSpeakerName, index) => {
                const updatedLines = [...editableTranscriptions];
                const line = updatedLines[index];
                if (!line) return;

                // Parse current static part to get current times (handle UUID prefix)
                const currentMatches = line.staticPart.match(/<[^>]+>\s*\[(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\]\s+((?:Speaker\s+)?[\w\s]+):/) || 
                                     line.staticPart.match(/\[(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\]\s+((?:Speaker\s+)?[\w\s]+):/);
                if (!currentMatches) return;

                const startTime = currentMatches[1];
                const endTime = currentMatches[2];
                
                // Extract UUID prefix if it exists
                const uuidMatch = line.staticPart.match(/(<[^>]+>\s*)/);
                const uuidPrefix = uuidMatch ? uuidMatch[1] : '';

                // Update the static part with new speaker, preserving UUID prefix
                updatedLines[index] = {
                    ...line,
                    staticPart: `${uuidPrefix}[${startTime}-${endTime}] ${newSpeakerName}:`
                };

                setEditableTranscriptions(updatedLines);
            };

            return (
                <div className="flex gap-2 items-center w-full">
                    {mode === "edit" ? (
                        <div className="flex gap-2 flex-col md:flex-row w-full">
                            <select
                                value={speakerName}
                                onChange={(e) => handleSpeakerChange(e.target.value, index)}
                                className="text-black rounded px-4 py-1 text-sm"
                                style={{ backgroundColor: getColorForSpeaker(speakerName) }}
                            >
                                <option value="Speaker A">Speaker A</option>
                                <option value="Speaker B">Speaker B</option>
                                <option value="Speaker C">Speaker C</option>
                                <option value="Speaker D">Speaker D</option>
                                <option value="Speaker E">Speaker E</option>
                                <option value="Unknown">Unknown</option>
                            </select>
                            <div className="cursor-pointer text-gray-400 flex gap-1 text-sm items-center">
                                <PlayCircleIcon className="w-5 h-5" onClick={() => seekToTime(startTimeSeconds)} />
                                {/* Start time inputs */}
                                {["Hours", "Minutes", "Seconds"].map((unit, unitIndex) => (
                                    <input
                                        key={`start-${unit}`}
                                        type="number"
                                        value={startTime[unitIndex]}
                                        onChange={(e) => handleTimeChange(e.target.value, 'start', unitIndex, false)}
                                        className="rounded px-2 py-1 text-xs w-9 input-number appearance-none text-center"
                                        min="0"
                                        placeholder={unit}
                                    />
                                ))}
                                <span>-</span>
                                {/* End time inputs */}
                                {["Hours", "Minutes", "Seconds"].map((unit, unitIndex) => (
                                    <input
                                        key={`end-${unit}`}
                                        type="number"
                                        value={endTime[unitIndex]}
                                        onChange={(e) => handleTimeChange(e.target.value, 'end', unitIndex, true)}
                                        className="rounded px-2 py-1 text-xs w-9 input-number appearance-none text-center"
                                        min="0"
                                        placeholder={unit}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="flex gap-2 items-center">
                                <span 
                                    style={{ backgroundColor: getColorForSpeaker(speakerName) }}
                                    className="text-black rounded-full px-4 py-1 text-sm whitespace-nowrap"
                                >
                                    {speakerName}
                                </span>
                                {timeVisibility && (
                                    <div 
                                        className="cursor-pointer underline text-gray-400 flex gap-1 text-sm items-center"
                                        onClick={() => seekToTime(startTimeSeconds)}
                                    >
                                        <PlayCircleIcon className="w-5 h-5" />
                                        {formatTime(startTimeSeconds, 0)} - {formatTime(endTimeSeconds, 0)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            );
        }
        return <div className="text-gray-600 text-sm">{staticPart.replace(/<[^>]+>/, '')}</div>;
    };

    // Function to get color for speaker
    const getColorForSpeaker = (speakerName) => {
        if (!speakerName) return '#9ca3af'; // gray-400 for null/undefined
        
        const normalizedName = speakerName.toString().trim();
        
        const speakerColors = {
            'Speaker A': '#f87171', // red-400
            'Speaker B': '#fbbf24', // amber-400  
            'Speaker C': '#34d399', // emerald-400
            'Speaker D': '#60a5fa', // blue-400
            'Speaker E': '#a78bfa', // violet-400
            'Unknown': '#9ca3af',   // gray-400
            // Additional mappings for common variations
            'A': '#f87171',
            'B': '#fbbf24',
            'C': '#34d399',
            'D': '#60a5fa',
            'E': '#a78bfa',
            'speaker a': '#f87171',
            'speaker b': '#fbbf24',
            'speaker c': '#34d399',
            'speaker d': '#60a5fa',
            'speaker e': '#a78bfa',
        };
        
        // Try exact match first
        if (speakerColors[normalizedName]) {
            return speakerColors[normalizedName];
        }
        
        // Try case-insensitive match
        const lowerName = normalizedName.toLowerCase();
        if (speakerColors[lowerName]) {
            return speakerColors[lowerName];
        }
        
        // Extract letter pattern (e.g., "SPEAKER_A" -> "A")
        const letterMatch = normalizedName.match(/[A-E]$/i);
        if (letterMatch) {
            const letter = letterMatch[0].toUpperCase();
            return speakerColors[letter] || '#9ca3af';
        }
        
        // Default fallback
        return '#9ca3af'; // gray-400
    };


    // Audio player functions
    const handlePlayPause = () => {
        setIsPlaying(!isPlaying);
    };

    const skip = (amount) => {
        if (audioRef.current) {
            let newTime = audioRef.current.currentTime + amount;
            newTime = Math.max(0, Math.min(newTime, audioRef.current.duration));
            audioRef.current.currentTime = newTime;
            const newProgress = (newTime / audioRef.current.duration) * 400;
            setProgress(newProgress);
        }
    };

    const handleProgressChange = (e) => {
        const value = e.target.value;
        if (audioRef.current) {
            audioRef.current.currentTime = (audioRef.current?.duration / 400) * value;
            setProgress(value);
        }
    };

    const startProgressLoop = () => {
        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            if (audioRef.current) {
                const { currentTime, duration } = audioRef.current;
                setProgress((currentTime / duration) * 400);
                setCurrentTime(currentTime);
            }
        }, 250);
    };

    // Edit mode functions
    const handleEdit = (index, newText) => {
        const updatedTranscriptions = editableTranscriptions.map((item, i) => 
            i === index ? { ...item, editablePart: newText } : item
        );
        setEditableTranscriptions(updatedTranscriptions);
    };

    const handleSave = async () => {
        setIsLoading(true);
        
        // Parse the editable transcriptions to reconstruct the proper format
        const editedUtterances = editableTranscriptions.map((item, index) => {
            // Parse the static part to extract time and speaker info
            const timeMatch = item.staticPart.match(/\[([0-9.]+)-([0-9.]+)\] ([^:]+):/);
            if (!timeMatch) {
                return null;
            }
            
            const [, startTime, endTime, speaker] = timeMatch;
            
            return {
                start: parseFloat(startTime) * 1000, // Already in seconds, convert to milliseconds
                end: parseFloat(endTime) * 1000,     // Already in seconds, convert to milliseconds 
                speaker: speaker.trim(),
                text: item.editablePart.trim(),
                channel: speaker.includes('Speaker A') ? 1 : (speaker.includes('Speaker B') ? 2 : null)
            };
        }).filter(Boolean); // Remove any null entries
        
        // Create the transcription object in the expected format
        const editedTranscription = {
            utterances: editedUtterances,
            // Keep some basic metadata
            status: "completed",
            text: editedUtterances.map(u => u.text).join(" "),
        };
        
        // Convert to JSON string for saving
        const newTranscription = JSON.stringify(editedTranscription);
        
        try {
            const result = await updateTranscription(analysisParentUuid, newTranscription);
            
            // Re-fetch the transcription data to get the updated version
            await loadTranscriptionData(true);
            setMode("view");
            setIsLoading(false);
        } catch (error) {
            console.error("Failed to update transcription", error);
            
            // Show user-friendly error message
            alert(`Failed to save transcription: ${error.response?.data?.error || error.message}`);
            setIsLoading(false);
        }
    };

    const handleSetEdit = () => {
        // Convert current transcription data to editable format
        if (transcriptionData?.transcription?.utterances) {
            const editableFormat = transcriptionData.transcription.utterances.map(utterance => {
                // Convert milliseconds to seconds for proper time display
                const startSeconds = (utterance.start || 0) / 1000;
                const endSeconds = (utterance.end || 0) / 1000;
                return {
                    staticPart: `<${uuidv4()}> [${startSeconds.toFixed(2)}-${endSeconds.toFixed(2)}] ${utterance.speaker || 'Unknown'}:`,
                    editablePart: utterance.text || ''
                };
            });
            setEditableTranscriptions(editableFormat);
        }
        setMode("edit");
    };

    const addNewBlock = (index) => {
        const newBlock = {
            staticPart: "<" + uuidv4() + "> [0-0] Unknown:",
            editablePart: '',
        };
        const start = editableTranscriptions.slice(0, index);
        const end = editableTranscriptions.slice(index);
        setEditableTranscriptions([...start, newBlock, ...end]);
    };

    const removeBlock = (index) => {
        let updatedTranscriptions = [...editableTranscriptions];
        updatedTranscriptions.splice(index, 1);
        setEditableTranscriptions(updatedTranscriptions);
    };

    // Audio effects
    useEffect(() => {
        const audio = audioRef.current;
        const updateCurrentTime = () => {
            setCurrentTime(audio.currentTime);
        };
        if (audio) {
            audio.addEventListener('timeupdate', updateCurrentTime);
        }
        return () => {
            if (audio) {
                audio.removeEventListener('timeupdate', updateCurrentTime);
            }
        };
    }, []);

    useEffect(() => {
        if (audioRef && audioRef.current) {
            if (isPlaying) {
                audioRef.current.play();
                startProgressLoop();
            } else {
                audioRef.current.pause();
            }
        }
        return () => clearInterval(intervalRef.current);
    }, [isPlaying]);

    useEffect(() => {
        if (audioRef?.current) {
            audioRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    if (loading) {
        return (
            <>
                {/* Floating reopen button when modal is hidden */}
                {!open && (
                    <div className="fixed bottom-4 right-4 z-50">
                        <button
                            onClick={reopenModal}
                            className="bg-iavisus-100 hover:bg-iavisus-200 text-white rounded-full p-3 shadow-lg transition-colors duration-200"
                            title={t('TranscriptionModal.ReopenTranscription', 'Reopen Transcription')}
                        >
                            <ClipboardIcon className="h-6 w-6" />
                        </button>
                    </div>
                )}

                <Transition.Root show={open} as={Fragment}>
                    <Dialog as="div" className="relative z-10" onClose={closeModal}>
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />
                        <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
                            <div className="bg-white rounded-lg p-8 max-w-md w-full">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-iavisus-100 mx-auto mb-4"></div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        {t('TranscriptionModal.Loading', 'Loading transcription...')}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {t('TranscriptionModal.LoadingDesc', 'Fetching transcription and emotion data')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Dialog>
                </Transition.Root>
            </>
        );
    }

    if (error) {
        return (
            <>
                {/* Floating reopen button when modal is hidden */}
                {!open && (
                    <div className="fixed bottom-4 right-4 z-50">
                        <button
                            onClick={reopenModal}
                            className="bg-iavisus-100 hover:bg-iavisus-200 text-white rounded-full p-3 shadow-lg transition-colors duration-200"
                            title={t('TranscriptionModal.ReopenTranscription', 'Reopen Transcription')}
                        >
                            <ClipboardIcon className="h-6 w-6" />
                        </button>
                    </div>
                )}

                <Transition.Root show={open} as={Fragment}>
                    <Dialog as="div" className="relative z-10" onClose={closeModal}>
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />
                        <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
                            <div className="bg-white rounded-lg p-8 max-w-md w-full">
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-red-900 mb-2">
                                        {t('TranscriptionModal.Error', 'Error Loading Transcription')}
                                    </h3>
                                    <p className="text-sm text-red-600 mb-4">{error}</p>
                                    <button
                                        className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700"
                                        onClick={closeModal}
                                    >
                                        {t('TranscriptionModal.CloseLabel', 'Close')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Dialog>
                </Transition.Root>
            </>
        );
    }

    const utterances = transcriptionData?.transcription?.utterances || [];
    const groupedUtterances = groupBySpeaker(utterances);
    const fileUrl = analysisParent?.file?.url;


    const fullTranscriptText = utterances
        .map(utterance => {
            const speaker = utterance.speaker || 'Unknown';
            const startTime = formatTimestamp(utterance.start || 0);
            const endTime = formatTimestamp(utterance.end || 0);
            return `[${startTime}-${endTime}] ${speaker}: ${utterance.text || ''}`;
        })
        .join('\n');

    return (
        <>
            {/* Floating reopen button when modal is hidden */}
            {!open && (
                <div className="fixed bottom-4 right-4 z-50">
                    <button
                        onClick={reopenModal}
                        className="bg-iavisus-100 hover:bg-iavisus-200 text-white rounded-full p-3 shadow-lg transition-colors duration-200"
                        title={t('TranscriptionModal.ReopenTranscription', 'Reopen Transcription')}
                    >
                        <ClipboardIcon className="h-6 w-6" />
                    </button>
                </div>
            )}

            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={() => {}}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-none">
                    <div className="flex min-h-full items-end justify-center text-center sm:items-center max-h-screen">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="flex flex-col max-h-screen relative transform overflow-none rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 w-full sm:p-6">
                                {/* Close button */}
                                <div className="absolute top-4 right-4 z-10">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-iavisus-100 focus:ring-offset-2"
                                        onClick={closeAndDestroy}
                                    >
                                        <span className="sr-only">{t('TranscriptionModal.Close', 'Close')}</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                                
                                <Dialog.Title
                                    as="h3"
                                    className="pb-4 text-lg font-medium leading-6 text-gray-500 text-center flex items-center justify-center gap-2"
                                >
                                    {t('TranscriptionModal.TranscriptionLabel', 'Transcription')}
                                    {emotionAvailable.hasAnyEmotions && (
                                        <span className="text-xs bg-iavisus-100 text-white px-2 py-1 rounded-full">
                                            {t('TranscriptionModal.WithEmotions', 'With Emotions')}
                                        </span>
                                    )}
                                </Dialog.Title>


                                {/* Controls */}
                                <div className="w-full flex flex-col md:flex-row justify-center md:justify-between mb-2 gap-2">
                                    <div className="w-full flex flex-col md:flex-row justify-center md:justify-start mb-2 gap-2">
                                        {/* View Toggle */}
                                        <button 
                                            className={"rounded-md text-sm border-2 border-black px-4 py-2 " + (!viewGrouped ? "bg-black text-white" : "bg-white text-black")}
                                            onClick={() => setViewGrouped(!viewGrouped)}
                                        >
                                            {viewGrouped ? (
                                                <div className="flex items-center gap-2">
                                                    <Bars3BottomLeftIcon className="w-4 h-4" />
                                                    {t('TranscriptionModal.Lines', 'Lines')}
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <Bars4Icon className="w-4 h-4" />
                                                    {t('TranscriptionModal.Paragraphs', 'Paragraphs')}
                                                </div>
                                            )}
                                        </button>

                                        {/* Time Toggle */}
                                        <button 
                                            className={"rounded-md text-sm border-2 border-black px-4 py-2 " + (timeVisibility ? "bg-black text-white" : "bg-white text-black")}
                                            onClick={() => setTimeVisibility(!timeVisibility)}
                                        >
                                            <div className="flex gap-2 items-center">
                                                <ClockIcon className="w-4 h-4" />
                                                {t('TranscriptionModal.Time', 'Time')}
                                            </div>
                                        </button>

                                        {/* Emotion Toggle */}
                                        {emotionAvailable.hasAnyEmotions && (
                                            <button 
                                                className={"rounded-md text-sm border-2 border-iavisus-100 px-4 py-2 " + (emotionVisibility ? "bg-iavisus-100 text-white" : "bg-white text-iavisus-100")}
                                                onClick={() => setEmotionVisibility(!emotionVisibility)}
                                            >
                                                <div className="flex gap-2 items-center">
                                                    {emotionVisibility ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
                                                    {t('TranscriptionModal.Emotions', 'Emotions')}
                                                </div>
                                            </button>
                                        )}
                                    </div>

                                    <div className="w-full flex justify-center md:justify-end mb-2 gap-2 max-w-7xl">
                                        <div className="flex gap-2">
                                            {/* Copy Button */}
                                            <CopyToClipboard text={fullTranscriptText}>
                                                <button className="border-2 border-black bg-white text-black rounded-md px-4 py-2 text-sm hover:bg-black hover:text-white flex justify-center items-center gap-2">
                                                    <ClipboardIcon className="w-4 h-4" />
                                                    {t('TranscriptionModal.CopyTextLabel', 'Copy Text')}
                                                </button>
                                            </CopyToClipboard>
                                            
                                            {/* Edit Button */}
                                            {view_mode === "private" && analysisParent && (
                                                <button 
                                                    className="border-2 border-black bg-black text-white rounded-md px-4 py-2 text-sm flex items-center justify-center gap-2"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        
                                                        if (mode === "view") {
                                                            handleSetEdit();
                                                        } else {
                                                            handleSave();
                                                        }
                                                    }}
                                                    disabled={isLoading}
                                                >
                                                    {isLoading ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                            {t('TranscriptionModal.SavingButton', 'Saving...')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <PencilIcon className="w-4 h-4" />
                                                            {mode === "view" ? t('TranscriptionModal.EditButton', 'Edit') : t('TranscriptionModal.SaveButton', 'Save')}
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>


                                {/* Transcription Content */}
                                <div className="overflow-y-auto w-full max-w-7xl mx-auto">
                                    {mode === "view" ? (
                                        // View Mode
                                        viewGrouped ? (
                                            // Grouped by speaker view
                                            groupedUtterances.map((group, groupIndex) => (
                                                <div key={`group-${groupIndex}-${group.speaker}`} className="text-base mb-4">
                                                    <div className="flex gap-2 items-center">
                                                        {/* Speaker badge with color */}
                                                        <span 
                                                            style={{ backgroundColor: getColorForSpeaker(group.speaker) }} 
                                                            className="text-black rounded-full px-4 py-1 text-sm whitespace-nowrap"
                                                        >
                                                            {group.speaker}
                                                        </span>
                                                        {timeVisibility && (
                                                            <div 
                                                                className="cursor-pointer underline text-gray-400 flex gap-1 text-sm items-center" 
                                                                onClick={() => seekToTime(group.startTime / 1000)}
                                                            >
                                                                <PlayCircleIcon className="w-5 h-5" />
                                                                <span>{formatTimestamp(group.startTime)} - {formatTimestamp(group.endTime)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="mt-2 ml-2">
                                                        {group.utterances.map((utterance, lineIndex) => (
                                                            <div key={`group-utterance-${lineIndex}-${utterance.start}`} className="mb-3">
                                                                {renderSentenceContent(utterance)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            // Individual utterances view
                                            utterances.map((utterance, index) => (
                                                <div key={`utterance-${index}-${utterance.start}`} className="text-base mb-4">
                                                    <div className="flex gap-2 items-center">
                                                        {/* Speaker badge with color */}
                                                        <span 
                                                            style={{ backgroundColor: getColorForSpeaker(utterance.speaker || 'Unknown') }} 
                                                            className="text-black rounded-full px-4 py-1 text-sm whitespace-nowrap"
                                                        >
                                                            {utterance.speaker || 'Unknown'}
                                                        </span>
                                                        {timeVisibility && (
                                                            <div 
                                                                className="cursor-pointer underline text-gray-400 flex gap-1 text-sm items-center" 
                                                                onClick={() => seekToTime(utterance.start / 1000)}
                                                            >
                                                                <PlayCircleIcon className="w-5 h-5" />
                                                                <span>{formatTimestamp(utterance.start || 0)} - {formatTimestamp(utterance.end || 0)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="mt-2 ml-2">
                                                        {renderSentenceContent(utterance)}
                                                    </div>
                                                </div>
                                            ))
                                        )
                                    ) : (
                                        // Edit Mode
                                        editableTranscriptions.map((line, index) => {
                                            if (line.staticPart.length > 1) {
                                                return (
                                                    <div key={index} className="flex flex-col gap-4 items-start my-2">
                                                        <div className="text-base whitespace-nowrap flex justify-between w-full">
                                                            <div>{transformTimeFormat(line.staticPart, index)}</div>
                                                            <div className="flex gap-2 items-center flex-col md:flex-row">
                                                                <button className="add-block-btn" onClick={() => addNewBlock(index)}>
                                                                    <PlusCircleIcon className="w-6 h-6" />
                                                                </button>
                                                                <button className="add-block-btn" onClick={() => removeBlock(index)}>
                                                                    <MinusCircleIcon className="w-6 h-6 text-red-600" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <textarea
                                                            value={line.editablePart}
                                                            onChange={(e) => handleEdit(index, e.target.value)}
                                                            className="flex border rounded w-full h-fit text-xs md:text-md"
                                                            rows={2}
                                                        />
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })
                                    )}
                                </div>

                                {/* Enhanced Audio Player Controls */}
                                {fileUrl && (
                                    <div className="w-full mb-8">
                                        {/* Progress Bar */}
                                        <input
                                            type="range"
                                            value={progress}
                                            min="0"
                                            max="400"
                                            onChange={handleProgressChange}
                                            className="range-slider w-full appearance-none bg-transparent mt-0"
                                            style={{ '--progress': `${(progress / 400) * 100}%` }}
                                        />
                                        
                                        <div className="flex flex-col md:flex-row items-center justify-between gap-2">
                                            {/* Custom Controls */}
                                            <div className="flex items-center md:gap-4 flex-col md:flex-row">
                                                <div className="flex items-center gap-2 justify-start mt-2 md:mt-0">
                                                    <button onClick={handlePlayPause}>
                                                        {isPlaying ? <PauseCircleIcon className="w-12 h-12" /> : <PlayCircleIcon className="w-12 h-12" />}
                                                    </button>
                                                    <span>{formatTime(currentTime) + " / "}<span className="text-sm text-gray-500">{formatTime(analysisParent?.duration || 0)}</span></span>
                                                </div>
                                                <div className="flex items-center justify-between gap-2 ml-2">
                                                    <button onClick={() => skip(-15)} className="bg-white border text-xs text-black px-1.5 py-0.5 rounded hover:bg-black hover:text-white">-15s</button>
                                                    <button onClick={() => skip(-5)} className="bg-white border text-xs text-black px-1.5 py-0.5 rounded hover:bg-black hover:text-white">-5s</button>
                                                    <button onClick={() => skip(-1)} className="bg-white border text-xs text-black px-1.5 py-0.5 rounded hover:bg-black hover:text-white">-1s</button>
                                                    <button onClick={() => skip(1)} className="bg-white border text-xs text-black px-1.5 py-0.5 rounded hover:bg-black hover:text-white">+1s</button>
                                                    <button onClick={() => skip(5)} className="bg-white border text-xs text-black px-1.5 py-0.5 rounded hover:bg-black hover:text-white">+5s</button>
                                                    <button onClick={() => skip(15)} className="bg-white border text-xs text-black px-1.5 py-0.5 rounded hover:bg-black hover:text-white">+15s</button>
                                                </div>
                                            </div>
                                            
                                            <div className="flex gap-3">
                                                {playSpeeds.map((playSpeed) => {
                                                    return (
                                                        playSpeed.speed === playbackRate ? (
                                                            <button 
                                                                key={playSpeed.name}
                                                                className="rounded-lg border border-black bg-black text-white w-10 md:w-12 h-6 md:h-8 flex justify-center items-center text-xs md:text-sm shadow-lg"
                                                            >
                                                                {playSpeed.name}
                                                            </button>
                                                        ) : (
                                                            <button
                                                                key={playSpeed.name}
                                                                onClick={() => { setPlaybackRate(playSpeed.speed) }}
                                                                className="rounded-lg border border-black bg-white text-black w-10 md:w-12 h-6 md:h-8 flex justify-center items-center text-xs md:text-sm shadow-lg"
                                                            >
                                                                {playSpeed.name}
                                                            </button>
                                                        )
                                                    );
                                                })}
                                            </div>
                                            
                                            {/* Hidden Audio Element */}
                                            <audio
                                                ref={audioRef}
                                                src={fileUrl}
                                                onEnded={() => setIsPlaying(false)}
                                                onPlay={() => setIsPlaying(true)}
                                                onPause={() => setIsPlaying(false)}
                                                hidden
                                            />
                                        </div>
                                    </div>
                                )}

                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
        </>
    );
}